import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "pl_install_dismissed_at";
const DISMISS_DAYS = 14;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari (legacy)
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  // Modern Apple devices report 'Mac' UA + touch points; iPadOS counts as desktop UA.
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
}

function wasRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    // Android / Chromium handler — fires when the browser deems the app installable.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS hint — no beforeinstallprompt support, so we show after a short delay
    // so we don't slap it on first paint.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => {
        if (!isStandalone() && !wasRecentlyDismissed()) {
          setIosHint(true);
          setShow(true);
        }
      }, 8000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // localStorage may be unavailable in privacy-strict mode — ignore.
    }
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice?.outcome === "dismissed") {
      dismiss();
    } else {
      setShow(false);
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-4 right-4 bottom-4 md:left-auto md:right-6 md:bottom-6 md:max-w-[360px] z-40 border border-black bg-white pl-hard-shadow-sm"
      role="dialog"
      aria-label="Install PrivacyLens"
    >
      <div className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 grid place-items-center bg-[#0047FF] text-white flex-shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#0047FF]">
            Install
          </div>
          <div className="font-display text-base tracking-tight mt-0.5">
            Get PrivacyLens on your home screen
          </div>
          {iosHint ? (
            <div className="text-xs text-[#475569] mt-1 leading-relaxed">
              Tap{" "}
              <Share className="inline w-3.5 h-3.5 -mt-0.5" aria-label="Share" />{" "}
              <strong>Share</strong> →{" "}
              <Plus className="inline w-3.5 h-3.5 -mt-0.5" aria-label="Add" />{" "}
              <strong>Add to Home Screen</strong> in Safari.
            </div>
          ) : (
            <div className="text-xs text-[#475569] mt-1 leading-relaxed">
              Faster scans, push-ready, works offline.
            </div>
          )}
          {!iosHint && (
            <button
              onClick={install}
              data-testid="install-prompt-install"
              className="mt-3 px-3 py-1.5 text-xs font-medium bg-[#0047FF] text-white border border-black hover:bg-[#0033CC]"
            >
              Install app →
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="install-prompt-dismiss"
          className="w-7 h-7 grid place-items-center hover:bg-[#F1F5F9] flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
